// src/components/PhishingFeedView.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Shield, Search, Edit, Save, X, GripVertical, AlertTriangle, Globe } from 'lucide-react';

const PhishingFeedView = ({ isSuperAdmin, setShowAddPhishingURL, setShowBulkUploadPhishing }) => {
  const { getToken, isSignedIn } = useAuth();

  // State
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFeed, setSelectedFeed] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRisk, setFilterRisk] = useState('all');
  const [filterSector, setFilterSector] = useState('all');
  const [filterCountry, setFilterCountry] = useState('all');
  const [isEditing, setIsEditing] = useState(false);
  const [editedFeed, setEditedFeed] = useState(null);
  
  // Resizable panel state
  const [leftPanelWidth, setLeftPanelWidth] = useState(420);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);

  const STATUS_OPTIONS = ['Active', 'Monitoring', 'Takedown Requested', 'Taken Down', 'Resolved'];
  const RISK_LEVELS = ['Critical', 'High', 'Medium', 'Low', 'Informational'];

  // Fetch phishing feed
  useEffect(() => {
    if (!isSignedIn) return;
    
    const fetchFeeds = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const res = await fetch('/api/phishing-feed', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!res.ok) throw new Error(`Failed to load phishing feed (${res.status})`);
        
        const data = await res.json();
        setFeeds(Array.isArray(data) ? data : []);
        
        if (data.length > 0 && !selectedFeed) {
          setSelectedFeed(data[0]);
        }
      } catch (e) {
        console.error('Phishing feed load error:', e);
        setError(e.message || 'Failed to load phishing feed');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFeeds();
  }, [isSignedIn, getToken]);

  // Handle mouse down on resizer
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Handle mouse move for resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || !containerRef.current) return;
      
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = e.clientX - containerRect.left;
      
      const minWidth = 280;
      const maxWidth = containerRect.width * 0.7;
      
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setLeftPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Get unique values for filters
  const uniqueSectors = [...new Set(feeds.map(f => f.target_sector).filter(Boolean))].sort();
  const uniqueCountries = [...new Set(feeds.map(f => f.target_country).filter(Boolean))].sort();

  // Filter feeds
  const filteredFeeds = feeds.filter(feed => {
    const matchesSearch = feed.url?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         feed.domain?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         feed.targeted_brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         feed.feed_id?.toString().includes(searchTerm);
    const matchesStatus = filterStatus === 'all' || feed.status === filterStatus;
    const matchesRisk = filterRisk === 'all' || feed.risk_level === filterRisk;
    const matchesSector = filterSector === 'all' || feed.target_sector === filterSector;
    const matchesCountry = filterCountry === 'all' || feed.target_country === filterCountry;
    return matchesSearch && matchesStatus && matchesRisk && matchesSector && matchesCountry;
  });

  // Get risk color
  const getRiskColor = (risk) => {
    const colors = {
      'Critical': 'bg-red-500/20 text-red-400 border-red-500/30',
      'High': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'Medium': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'Low': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'Informational': 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return colors[risk] || colors['Medium'];
  };

  // Get status color
  const getStatusColor = (status) => {
    if (status === 'Active') return 'bg-red-500/20 text-red-400 border-red-500/30';
    if (status?.includes('Takedown')) return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    if (status?.includes('Taken Down')) return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    if (status?.includes('Resolved')) return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (status === 'Monitoring') return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
    return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  // Handle edit
  const handleEdit = () => {
    setEditedFeed({ ...selectedFeed });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditedFeed(null);
    setIsEditing(false);
  };

  // Save changes
  const handleSave = async () => {
    if (!editedFeed) {
      alert('No entry selected.');
      return;
    }

    try {
      const token = await getToken();
      const response = await fetch(`/api/phishing-feed/${editedFeed.feed_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          target_sector: editedFeed.target_sector,
          target_country: editedFeed.target_country,
          targeted_brand: editedFeed.targeted_brand,
          phishing_type: editedFeed.phishing_type,
          risk_level: editedFeed.risk_level,
          status: editedFeed.status,
          first_seen_date: editedFeed.first_seen_date,
          ip_address: editedFeed.ip_address,
          hosting_provider: editedFeed.hosting_provider,
          registrar: editedFeed.registrar,
          ssl_certificate: editedFeed.ssl_certificate,
          detection_method: editedFeed.detection_method,
          source: editedFeed.source,
          threat_actor: editedFeed.threat_actor,
          campaign_id: editedFeed.campaign_id,
          page_title: editedFeed.page_title,
          screenshot_url: editedFeed.screenshot_url,
          notes: editedFeed.notes,
          takedown_requested_date: editedFeed.takedown_requested_date,
          taken_down_date: editedFeed.taken_down_date
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `Failed to update entry (HTTP ${response.status})`);
      }

      const updated = await response.json();

      // Update local state
      setFeeds(feeds.map(f => 
        f.feed_id === updated.feed_id ? updated : f
      ));
      setSelectedFeed(updated);
      setIsEditing(false);
      setEditedFeed(null);
      alert(`Entry updated successfully!`);
    } catch (error) {
      console.error('Error saving entry:', error);
      alert(`Error: ${error.message}`);
    }
  };

  return (
    <div ref={containerRef} className="h-full flex relative">
      {/* LEFT SIDEBAR - Feed List */}
      <div 
        className="border-r border-slate-800/50 bg-slate-900/30 flex flex-col"
        style={{ width: `${leftPanelWidth}px`, minWidth: '280px', maxWidth: '70%' }}
      >
        <div className="px-4 py-4 border-b border-slate-800/50 bg-gradient-to-r from-slate-900/50 to-slate-900/30">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
              <Shield className="w-5 h-5 text-cyan-400" />
              Phishing Feed
            </h2>
            {isSuperAdmin && (
              <div className="flex gap-2">
                <button
                  onClick={() => setShowAddPhishingURL(true)}
                  className="px-3 py-1.5 bg-cyan-500 hover:bg-cyan-600 rounded-lg text-xs text-white font-medium transition-all"
                >
                  + Add
                </button>
                <button
                  onClick={() => setShowBulkUploadPhishing(true)}
                  className="px-3 py-1.5 bg-purple-500 hover:bg-purple-600 rounded-lg text-xs text-white font-medium transition-all"
                >
                  + Add CSV
                </button>
              </div>
            )}
          </div>

          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search URLs, domains, brands..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20"
            />
          </div>

          <div className="grid grid-cols-2 gap-2 mb-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-2 py-1.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-slate-200"
            >
              <option value="all">All Status</option>
              {STATUS_OPTIONS.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
            <select
              value={filterRisk}
              onChange={(e) => setFilterRisk(e.target.value)}
              className="px-2 py-1.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-slate-200"
            >
              <option value="all">All Risk</option>
              {RISK_LEVELS.map(risk => (
                <option key={risk} value={risk}>{risk}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <select
              value={filterSector}
              onChange={(e) => setFilterSector(e.target.value)}
              className="px-2 py-1.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-slate-200"
            >
              <option value="all">All Sectors</option>
              {uniqueSectors.map(sector => (
                <option key={sector} value={sector}>{sector}</option>
              ))}
            </select>
            <select
              value={filterCountry}
              onChange={(e) => setFilterCountry(e.target.value)}
              className="px-2 py-1.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-slate-200"
            >
              <option value="all">All Countries</option>
              {uniqueCountries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>

          <div className="mt-3 text-xs text-slate-400">
            {filteredFeeds.length} URL{filteredFeeds.length !== 1 ? 's' : ''}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {error && (
            <div className="m-4 bg-red-500/10 border border-red-500/30 rounded p-3 text-sm text-red-300">
              {error}
            </div>
          )}
          {loading && (
            <div className="p-4 text-sm text-slate-400">Loading phishing feed...</div>
          )}
          {!loading && filteredFeeds.length === 0 && (
            <div className="p-4 text-sm text-slate-500">No phishing URLs found.</div>
          )}
          {!loading && filteredFeeds.map((feed) => {
            const isSelected = selectedFeed?.feed_id === feed.feed_id;
            
            return (
              <button
                key={feed.feed_id}
                onClick={() => {
                  setSelectedFeed(feed);
                  setIsEditing(false);
                  setEditedFeed(null);
                }}
                className={`w-full p-4 border-b border-slate-800/30 text-left transition-all ${
                  isSelected
                    ? 'bg-cyan-500/10 border-l-4 border-l-cyan-500'
                    : 'hover:bg-slate-800/30 border-l-4 border-l-transparent'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    {feed.targeted_brand && feed.targeted_brand !== 'N/A' && feed.targeted_brand !== 'n/a' && (
                      <span className="text-xs text-slate-400">{feed.targeted_brand}</span>
                    )}
                  </div>
                  <span className="text-xs text-slate-500">{feed.first_seen_date || '-'}</span>
                </div>

                <div className="text-sm font-medium text-slate-200 mb-1 font-mono truncate">
                  {feed.domain}
                </div>

                <div className="text-xs text-slate-400 mb-2 truncate">
                  {feed.url}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(feed.status)}`}>
                    {feed.status}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getRiskColor(feed.risk_level)}`}>
                    {feed.risk_level}
                  </span>
                  {feed.target_sector && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs border border-slate-600 text-slate-400">
                      {feed.target_sector}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* RESIZABLE SEPARATOR */}
      <div
        className={`w-1 bg-slate-800/50 hover:bg-cyan-500/50 cursor-col-resize flex items-center justify-center transition-colors relative group ${
          isResizing ? 'bg-cyan-500/70' : ''
        }`}
        onMouseDown={handleMouseDown}
      >
        <div className="absolute inset-y-0 -left-1 -right-1 flex items-center justify-center">
          <GripVertical className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 transition-colors" />
        </div>
      </div>

      {/* RIGHT PANEL - Feed Details */}
      <div className="flex-1 overflow-y-auto">
        {selectedFeed ? (
          <div className="p-8">
            {/* Details content continues in next message... */}
            <div className="mb-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Globe className="w-6 h-6 text-red-400" />
                    <h2 className="text-2xl font-bold text-slate-100 font-mono break-all">
                      {selectedFeed.domain}
                    </h2>
                  </div>
                  <div className="text-sm text-slate-400 mb-2">
                    <span className="font-mono">ID: {selectedFeed.feed_id}</span>
                  </div>
                 
<div className="text-sm text-slate-300 break-all mb-2">
  {selectedFeed.url}
</div>



                </div>
                {!isEditing && (
                  <button
                    onClick={() => setSelectedFeed(null)}
                    className="p-2 hover:bg-slate-800/50 rounded-lg transition-all"
                  >
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                )}
              </div>

              {/* Status Badges */}
              <div className="flex items-center gap-2 mb-4">
                {isEditing ? (
                  <>
                    <select
                      value={editedFeed.status}
                      onChange={(e) => setEditedFeed({ ...editedFeed, status: e.target.value })}
                      className="px-3 py-1 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200"
                    >
                      {STATUS_OPTIONS.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                    <select
                      value={editedFeed.risk_level}
                      onChange={(e) => setEditedFeed({ ...editedFeed, risk_level: e.target.value })}
                      className="px-3 py-1 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200"
                    >
                      {RISK_LEVELS.map(risk => (
                        <option key={risk} value={risk}>{risk}</option>
                      ))}
                    </select>
                  </>
                ) : (
                  <>
                    <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium border ${getStatusColor(selectedFeed.status)}`}>
                      {selectedFeed.status}
                    </span>
                    <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium border ${getRiskColor(selectedFeed.risk_level)}`}>
                      {selectedFeed.risk_level} Risk
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-6">
              {/* Classification */}
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    Targeted Brand
                  </h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedFeed.targeted_brand || ''}
                      onChange={(e) => setEditedFeed({ ...editedFeed, targeted_brand: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                      placeholder="e.g., PayPal"
                    />
                  ) : (
                    <p className="text-slate-200">{selectedFeed.targeted_brand || '-'}</p>
                  )}
                </div>

                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    Phishing Type
                  </h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedFeed.phishing_type || ''}
                      onChange={(e) => setEditedFeed({ ...editedFeed, phishing_type: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                      placeholder="e.g., Credential Harvesting"
                    />
                  ) : (
                    <p className="text-slate-200">{selectedFeed.phishing_type || '-'}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    Target Sector
                  </h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedFeed.target_sector || ''}
                      onChange={(e) => setEditedFeed({ ...editedFeed, target_sector: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                      placeholder="e.g., Financial"
                    />
                  ) : (
                    <p className="text-slate-200">{selectedFeed.target_sector || '-'}</p>
                  )}
                </div>

                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    Target Country
                  </h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedFeed.target_country || ''}
                      onChange={(e) => setEditedFeed({ ...editedFeed, target_country: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                      placeholder="e.g., USA"
                    />
                  ) : (
                    <p className="text-slate-200">{selectedFeed.target_country || '-'}</p>
                  )}
                </div>
              </div>

              {/* Technical Details */}
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    IP Address
                  </h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedFeed.ip_address || ''}
                      onChange={(e) => setEditedFeed({ ...editedFeed, ip_address: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200 font-mono"
                      placeholder="192.168.1.1"
                    />
                  ) : (
                    <p className="text-slate-200 font-mono">{selectedFeed.ip_address || '-'}</p>
                  )}
                </div>

                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    Hosting Provider
                  </h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedFeed.hosting_provider || ''}
                      onChange={(e) => setEditedFeed({ ...editedFeed, hosting_provider: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                      placeholder="e.g., Cloudflare"
                    />
                  ) : (
                    <p className="text-slate-200">{selectedFeed.hosting_provider || '-'}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    First Seen
                  </h3>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editedFeed.first_seen_date || ''}
                      onChange={(e) => setEditedFeed({ ...editedFeed, first_seen_date: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                    />
                  ) : (
                    <p className="text-slate-200">{selectedFeed.first_seen_date || '-'}</p>
                  )}
                </div>

                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    Source
                  </h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedFeed.source || ''}
                      onChange={(e) => setEditedFeed({ ...editedFeed, source: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                      placeholder="e.g., PhishTank"
                    />
                  ) : (
                    <p className="text-slate-200">{selectedFeed.source || '-'}</p>
                  )}
                </div>
              </div>

              {/* Notes */}
              {(selectedFeed.notes || isEditing) && (
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                    Notes
                  </h3>
                  {isEditing ? (
                    <textarea
                      rows={4}
                      value={editedFeed.notes || ''}
                      onChange={(e) => setEditedFeed({ ...editedFeed, notes: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                      placeholder="Additional details..."
                    />
                  ) : (
                    <p className="text-slate-200 whitespace-pre-wrap">{selectedFeed.notes}</p>
                  )}
                </div>
              )}

              {/* Metadata */}
              <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Metadata
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Created:</span>
                    <span className="ml-2 text-slate-200">
                      {selectedFeed.created_at ? new Date(selectedFeed.created_at).toLocaleString() : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Created By:</span>
                    <span className="ml-2 text-slate-200">{selectedFeed.created_by || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Updated:</span>
                    <span className="ml-2 text-slate-200">
                      {selectedFeed.updated_at ? new Date(selectedFeed.updated_at).toLocaleString() : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Updated By:</span>
                    <span className="ml-2 text-slate-200">{selectedFeed.updated_by || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons - Super Admin Only */}
              {isSuperAdmin && (
                <div className="flex gap-3">
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-sm text-white transition-all flex items-center gap-2"
                      >
                        <Save className="w-4 h-4" />
                        Save Changes
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-slate-200 transition-all"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleEdit}
                      className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-slate-200 transition-all flex items-center gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      Edit Entry
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <Shield className="w-16 h-16 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-400">Select a phishing URL to view details</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhishingFeedView;